import { useMutation, useQuery } from "@tanstack/react-query";
import {
  programService,
  ProgramsFindAllPayload,
} from "@/services/program.service";
import { ProgramPayload } from "@/domain/programs.interface";
import { useProgramsByUser } from "./useUsers";

export const useGetPrograms = ({ sortType = "asc" }: { sortType?: string }) => {
  const {
    data: programs,
    isLoading: isLoadingPrograms,
    isFetching: isFetchingPrograms,
    refetch: refetchPrograms,
  } = useQuery({
    queryKey: ["get-programs"],
    queryFn: () => programService.getPrograms({ sortType }),
    staleTime: 1 * 60 * 60 * 1000,
  });

  return {
    programs,
    isLoadingPrograms,
    isFetchingPrograms,
    refetchPrograms,
  };
};

export const useGetUserPrograms = ({
  userId,
  sortType = "asc",
  hasSystemAdminRole,
}: {
  userId: string;
  sortType?: string;
  hasSystemAdminRole: boolean;
}) => {
  if (hasSystemAdminRole) {
    return useGetPrograms({ sortType });
  } else {
    return useProgramsByUser({ userId, sortType });
  }
};

export const useProgramsPaginated = (filters: ProgramsFindAllPayload) => {
  const {
    data: programsPaginated,
    isLoading: isLoadingProgramsPaginated,
    isFetching: isFetchingProgramsPaginated,
    refetch: refetchProgramsPaginated,
  } = useQuery({
    queryKey: ["parca-programs-list"],
    queryFn: () => programService.findAll(filters),
  });

  return {
    programsPaginated,
    isLoadingProgramsPaginated,
    isFetchingProgramsPaginated,
    refetchProgramsPaginated,
  };
};

export const useCreateProgram = () => {
  return useMutation({
    mutationFn: (payload: ProgramPayload) => programService.createOne(payload),
  });
};

export const useUpdateProgram = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ProgramPayload }) =>
      programService.updateOne({ id, payload }),
  });
};

export const useArchiveProgram = () => {
  return useMutation({
    mutationFn: (id: string) => programService.archiveOne(id),
  });
};
